/**
 * Which chrome a discovery page (`/leagues`, `/orgs`, `/news`, …) renders in.
 *
 * Those pages are reachable from BOTH navigation systems, so neither shell is
 * right for everyone. The rule is: stay in the shell you were already browsing.
 *
 *  - Signed in → always the app shell. There is no marketing context to keep.
 *  - Signed out, arrived from the app shell (the dashboard sidebar) → app shell.
 *  - Signed out, arrived from the marketing site, or landed cold → marketing.
 *
 * The first version decided on auth state alone, which made the signed-out
 * dashboard sidebar a trap: every item in it is a discovery page, so every
 * click ejected the visitor onto the marketing site with the sidebar gone.
 */

export type Shell = 'app' | 'public'

/** Per-tab: the shell a visitor is in is a property of this browsing session. */
const STORAGE_KEY = 'carousel_shell'

export interface ShellInput {
  /** Auth is still resolving; `hasSession` is the best early signal. */
  isLoading: boolean
  isAuthenticated: boolean
  /** A stored session exists — hydrated synchronously, before auth resolves. */
  hasSession: boolean
  /** The shell this tab was last browsing in, if any. */
  lastShell: Shell | null
}

export function chooseShell(input: ShellInput): Shell {
  // Before auth settles, a stored session is the right early guess, so a cold
  // load paints the correct chrome first time instead of swapping. Once it
  // settles, the authoritative flag wins — an expired session is signed out.
  const signedIn = input.isLoading ? input.hasSession : input.isAuthenticated
  if (signedIn) return 'app'
  return input.lastShell === 'app' ? 'app' : 'public'
}

type Reader = Pick<Storage, 'getItem'>
type Writer = Pick<Storage, 'setItem'>

function defaultStorage(): Storage | undefined {
  try {
    return typeof sessionStorage === 'undefined' ? undefined : sessionStorage
  } catch {
    // Access can throw outright (blocked storage, some privacy modes).
    return undefined
  }
}

export function readShell(storage: Reader | undefined = defaultStorage()): Shell | null {
  try {
    const value = storage?.getItem(STORAGE_KEY)
    return value === 'app' || value === 'public' ? value : null
  } catch {
    return null
  }
}

/**
 * Record the shell being shown. Losing this only costs the preference — the
 * visitor falls back to marketing chrome — so failures are swallowed.
 */
export function writeShell(shell: Shell, storage: Writer | undefined = defaultStorage()): void {
  try {
    storage?.setItem(STORAGE_KEY, shell)
  } catch {
    // Storage full or blocked; the fallback is still a working page.
  }
}
